import { setupPriceSliders } from '../../../features/priceRange/scripts';
import { debounce } from '../../../scripts/util/debounce';
import { handleize } from '../../../scripts/util/handleize';

if (!customElements.get('nosto-facets')) {
  class NostoFacets extends HTMLElement {
    constructor() {
      super();

      this.isMobile();
      window.addEventListener('resize', this.isMobile.bind(this), { passive: true });

      this.debouncedOnFormChange = debounce((event) => {
        this.onFormChange(event);
      }, 100);
    }

    connectedCallback() {
      this.setupEventListeners();
      setupPriceSliders();
    }

    get lastDeviceType() {
      return null;
    }
    set lastDeviceType(value) {
      this._lastDeviceType = value;
    }

    get nostoCollection() {
      return this.closest('nosto-collection') || document.querySelector('nosto-collection');
    }
    get facetsForm() {
      return this.querySelector('form[data-facets-form]');
    }
    get nostoFacetsElement() {
      if (this.facetsForm) return this.facetsForm.querySelector('[data-nosto-facets]');
    }
    get filterTemplate() {
      return this.querySelector('template.nosto-facets__template').content.cloneNode(true);
    }
    get filterCheckboxTemplate() {
      return this.querySelector('template.nosto-facets__template--checkbox').content.cloneNode(true);
    }
    get filterPriceTemplate() {
      return this.querySelector('template.nosto-facets__template--price').content.cloneNode(true);
    }
    get details() {
      return this.querySelector('details');
    }
    get facetInputs() {
      return this.querySelectorAll('input[type="checkbox"]');
    }
    get activeFacetsElement() {
      return this.querySelector('[data-active-facets]');
    }
    get activeFacetsForm() {
      if (this.activeFacetsElement) return this.activeFacetsElement.querySelector('form[data-active-facets-form]');
    }
    get activeFacetsList() {
      if (this.activeFacetsElement) return this.activeFacetsElement.querySelector('.active-facets');
    }
    get activeFacetsTemplate() {
      return this.querySelector('template.nosto-facets__template--active_facet').content.cloneNode(true);
    }

    setupEventListeners() {
      this.classList.remove('hidden');
      if (!this.nostoCollection) {
        this.classList.add('hidden');
        console.error('Nosto collection element not found');
        return;
      }

      if (this.facetsForm) this.facetsForm.addEventListener('change', this.debouncedOnFormChange.bind(this));

      document.addEventListener('pointerdown', this.onPointerdown.bind(this));
      if (this.details) this.details.addEventListener('toggle', this.onToggle.bind(this));

      this.nostoCollection.addEventListener('nosto:products-loaded', (event) => {
        const products = event.detail.products;
        this.facets = products.facets;
        if (this.facets) {
          if (event.detail.initial) {
            this.renderFilters(); // full initial render
          } else {
            this.updateFilterStates(); // update disabled/selected states
          }
          this.renderActiveFacets();
        }
      });
    }

    onFormChange(e) {
      e.preventDefault();
      this.URLUpdate();
      this.renderActiveFacets();

      const event = new CustomEvent('nosto:products-reloaded', {
        bubbles: true,
      });
      this.dispatchEvent(event);
    }

    isMobile() {
      const currentDeviceType = window.innerWidth > 768 ? 'desktop' : 'mobile';
      if (!this.lastDeviceType || currentDeviceType !== this.lastDeviceType) {
        if (currentDeviceType === 'desktop') {
          this.desktopFunc();
        } else {
          this.mobileFunc();
        }
        this.lastDeviceType = currentDeviceType;
      }
    }

    desktopFunc() {
      const facet = this.closest('.facet-main__wrapper');
      const targetContainer = document.querySelector('.facet-main__wrapper-desktop');
      if (facet && targetContainer) {
        targetContainer.appendChild(facet);
      }
    }

    mobileFunc() {
      const facet = this.closest('.facet-main__wrapper');
      const targetContainer = document.querySelector('.facet-main__wrapper-mobile');
      if (facet && targetContainer) {
        targetContainer.appendChild(facet);
        if (facet.classList.contains('d-md-flex') && facet.classList.contains('d-none')) {
          facet.classList.remove('d-md-flex', 'd-none');
        }
      }
    }

    onPointerdown(event) {
      if (!this.details.contains(event.target)) {
        this.details.removeAttribute('open');
      }
    }

    onToggle(e) {
      const details = e.target.closest('details');
      const content = details.querySelector('.nosto-facets__content');
      if (details.hasAttribute('open')) {
        content.focus();
      }
    }

    renderFilters() {
      if (!this.nostoFacetsElement) {
        console.error('Nosto filters element not found');
        return;
      }

      // Clear existing filters
      this.nostoFacetsElement.innerHTML = '';

      if (!this.facets || this.facets.length < 1) {
        console.error('No facets found');
        return;
      }
      const urlParams = new URLSearchParams(window.location.search);

      this.facets.forEach((facet, index) => {
        const filterGroup = this.filterTemplate;
        const details = filterGroup.querySelector('details');
        const summaryLabel = filterGroup.querySelector('[data-nosto-filter-label]');
        details.setAttribute('data-index', index);
        summaryLabel.textContent = facet.name;

        if (facet.field === 'price') {
          this.renderPriceFilter(urlParams, filterGroup, facet);
        } else {
          this.renderFiltersValues(urlParams, filterGroup, facet);
        }

        this.nostoFacetsElement.appendChild(filterGroup);
      });
    }

    renderPriceFilter(urlParams, filterGroup, facet) {
      const filterValuesWrapper = filterGroup.querySelector('.nosto-facets__values-wrapper');
      const minPrice = facet.min;
      const maxPrice = facet.max;
      const minPriceValue = urlParams.get('price_from') || minPrice;
      const maxPriceValue = urlParams.get('price_to') || maxPrice;
      const filterPriceTemplate = this.filterPriceTemplate;
      const priceRange = filterPriceTemplate.querySelector('price-range-slider');

      if (priceRange) {
        priceRange.dataset.minPrice = minPrice;
        priceRange.dataset.maxPrice = maxPrice;
        priceRange.dataset.minPriceValue = minPriceValue;
        priceRange.dataset.maxPriceValue = maxPriceValue;
      }

      filterValuesWrapper.innerHTML = priceRange.outerHTML;
    }

    renderFiltersValues(urlParams, filterGroup, facet) {
      const ul = filterGroup.querySelector('[data-nosto-filter-values]');
      const sortedData = facet.data.sort() || facet.data;

      sortedData.forEach((facetValue, i) => {
        const checkboxTemplate = this.filterCheckboxTemplate;
        const li = checkboxTemplate.querySelector('li');
        const input = li.querySelector('input');
        const label = li.querySelector('label');
        const labelSpan = label.querySelector('span.nosto-facets__label-value');

        const id = `Filter-${facet.field}-${i + 1}`;
        const facetValueValue = facetValue.value;

        input.id = id;
        input.name = facet.field;
        input.value = facetValueValue;
        input.disabled = true;

        const selectedValues = urlParams.getAll(facet.field);
        input.checked = selectedValues.includes(facetValueValue);

        ul.classList.add(`nosto-facets__${handleize(facet.name)}--type`);

        if (facet.field.includes('custom-color-filter')) label.dataset.color = facetValueValue.toLowerCase();
        label.setAttribute('for', id);
        if (facet.field === 'sort_by') {
          input.type = 'radio';
          labelSpan.textContent = facetValue.label;
        } else {
          labelSpan.textContent = this.parseIfJSON(facetValueValue);
        }

        ul.appendChild(li);
      });
    }

    updateFilterStates() {
      this.facetInputs.forEach((input) => {
        input.disabled = true;
      });
      const urlParams = new URLSearchParams(window.location.search);
      this.facets.forEach((facet) => {
        const filterGroup = this.filterTemplate;
        if (facet.field === 'price') {
          this.renderPriceFilter(urlParams, filterGroup, facet);
        } else {
          facet.data?.forEach((facetValue) => {
            const selector = `input[name="${facet.field}"][value='${facetValue.value}']`;
            const input = this.querySelector(selector);
            if (input) {
              input.checked = facetValue.selected;
              if (facet.field === 'sort_by') {
                input.type = 'radio';
                input.disabled = false;
              } else {
                input.disabled = !facetValue.count;
              }
            }
          });
        }
        const priceRange = this.facetsForm.querySelector('price-range-slider');
        const priceRangeTemplate = filterGroup.querySelector('price-range-slider');
        if (priceRange && priceRangeTemplate) priceRange.replaceWith(priceRangeTemplate);
      });
    }

    renderActiveFacets() {
      this.activeFacetsElement.classList.add('hidden');
      if (!this.activeFacetsList || !this.facetsForm) return;

      // Remove previously rendered active facets EXCEPT the "clear all" button
      const facetButtons = this.activeFacetsList.querySelectorAll('.active-facets__button-wrapper:not([data-remove-all-facet])');
      facetButtons.forEach((el) => el.remove());
      const removeAllButton = this.activeFacetsList.querySelector('[data-remove-all-facet]');
      if (removeAllButton) {
        removeAllButton.addEventListener('click', (e) => {
          e.preventDefault();
          this.facetsForm.reset();
          this.facetsForm.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      const priceFacet = this.facets.find((facet) => facet.field === 'price');
      let min = 0;
      let max = 0;
      if (priceFacet) {
        min = priceFacet.min;
        max = priceFacet.max;
      }

      const formData = new FormData(this.facetsForm);
      const entries = Array.from(formData.entries());

      const priceFrom = formData.get('price_from');
      const priceTo = formData.get('price_to');

      const showPrice = parseFloat(priceFrom) !== min || parseFloat(priceTo) !== max;

      if (showPrice) {
        const template = this.activeFacetsTemplate;
        const wrapper = template.querySelector('.active-facets__button-wrapper').cloneNode(true);
        const link = wrapper.querySelector('a');

        if (link) {
          const from = priceFrom ? priceFrom : min;
          const to = priceTo ? priceTo : max;

          const linkURL = new URL(window.location.href);
          const params = new URLSearchParams(linkURL.search);
          params.delete('price_from');
          params.delete('price_to');
          linkURL.search = params.toString();

          const linkSpan = link.querySelector('span');
          if (linkSpan) linkSpan.textContent = `${Shopify.formatMoney(from * 100)} – ${Shopify.formatMoney(to * 100)}`;
          link.href = linkURL.pathname + (linkURL.search ? `?${linkURL.search}` : '');
          this.activeFacetsElement.classList.remove('hidden');

          link.addEventListener('click', (e) => {
            e.preventDefault();
            const fromInput = this.facetsForm.querySelector('[name="price_from"]');
            const toInput = this.facetsForm.querySelector('[name="price_to"]');
            if (fromInput) fromInput.value = parseFloat(fromInput.min);
            if (toInput) toInput.value = parseFloat(toInput.max);
            this.facetsForm.dispatchEvent(new Event('change', { bubbles: true }));
          });
        }

        this.activeFacetsList.insertAdjacentElement('afterbegin', wrapper);
      }

      entries.forEach(([name, value]) => {
        if (!value || ['sort_by', 'price_from', 'price_to'].includes(name)) return;
        this.activeFacetsElement.classList.remove('hidden');

        const template = this.activeFacetsTemplate;
        const wrapper = template.querySelector('.active-facets__button-wrapper').cloneNode(true);
        const link = wrapper.querySelector('a');

        if (link) {
          const facetLabel = this.facets.find((facet) => facet.field === name)?.name || name;
          const linkURL = new URL(window.location.href);
          const params = new URLSearchParams(linkURL.search);

          params.delete(name);
          params.delete(value);
          linkURL.search = params.toString();

          const linkSpan = link.querySelector('span');
          if (linkSpan) linkSpan.textContent = `${facetLabel}: ${this.parseIfJSON(value)}`;
          link.href = linkURL.pathname + (linkURL.search ? `?${linkURL.search}` : '');

          link.addEventListener('click', (e) => {
            e.preventDefault();
            this.removeActiveFacet(name, value);
          });
        }

        this.activeFacetsList.insertAdjacentElement('afterbegin', wrapper);
      });
    }

    URLUpdate() {
      if (!this.facetsForm) {
        console.error('Nosto facets form not found');
        return;
      }

      const formData = new FormData(this.facetsForm);
      const params = new URLSearchParams(formData);

      const priceFacet = this.facets?.find((f) => f.field === 'price');
      const min = priceFacet?.min ?? 0;
      const max = priceFacet?.max ?? Infinity;
      const priceFrom = params.get('price_from');
      const priceTo = params.get('price_to');

      const from = parseFloat(priceFrom);
      const to = parseFloat(priceTo);

      if (from > to) {
        params.set('price_from', to);
      }

      // Remove both only if both match defaults
      const removePriceParams = min === from && max === to;

      if (removePriceParams || isNaN(from) || isNaN(to)) {
        params.delete('price_from');
        params.delete('price_to');
      }

      const query = params.toString();

      window.history.pushState({ params: query }, '', `${window.location.pathname}${query && '?'.concat(query)}`);
    }

    removeActiveFacet(name, value) {
      const input = this.facetsForm.querySelector(`input[name="${name}"][value='${value}']`);
      if (input) {
        input.checked = false;
        const event = new Event('change', { bubbles: true });
        input.dispatchEvent(event);
      }
    }

    parseIfJSON(data) {
      try {
        return JSON.parse(data);
      } catch (e) {
        return data;
      }
    }
  }
  customElements.define('nosto-facets', NostoFacets);
}
